import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import DynamicForm from '../components/DynamicForm';

function PaymentPanel({ booking, eventInfo, onPaid }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [changingMethod, setChangingMethod] = useState(false);

  const pay = async (planId) => {
    setError(''); setLoading(planId);
    try {
      const id = booking.bookingId || booking.id;
      const res = await api.request('POST',
        `/api/payments/bookings/${id}/checkout/${planId}`,
        {}, token);
      if (res.url) window.location.href = res.url;
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  const payments = booking.payments || [];
  const paymentStatus = booking.paymentStatus;
  const totalDue = booking.totalDue || 0;
  const totalPaid = booking.totalPaid || 0;
  const pending = totalDue - totalPaid;
  const isCompleted = paymentStatus === 'completed';
  const today = new Date();

  // Estancia pagada por transferencia: la familia ve los datos bancarios en vez de Stripe.
  const porTransferencia = booking.bookingType === 'estancia' && booking.metodoPago === 'transferencia';
  const dt = booking.datosTransferencia || {};

  // Calcular qué métodos están disponibles para cambio
  const singleDueDate = booking.singlePaymentDueDate ? new Date(booking.singlePaymentDueDate) : null;
  const singleExpired = singleDueDate && today > singleDueDate;
  // Formas de pago del evento (formato nuevo: paymentOptions). paymentPlans
  // es el formato antiguo (solo la primera opción) y queda de último recurso.
  const paymentOptions = (booking.paymentOptions || []).filter(o => o?.id);
  const currentMethod = booking.selectedPlan || 'single';
  const isCurrentSingle = currentMethod === 'single';
  const currentOption = booking.selectedPaymentOption
    || paymentOptions.find(o => o.id === currentMethod)
    || null;
  const optionPlans = currentOption?.plans || (!isCurrentSingle ? (booking.paymentPlans || []) : []);
  const hasSingle = booking.allowSinglePayment !== false;
  const hasOtherOptions = paymentOptions.length > 0;
  const anyPaid = totalPaid > 0 || payments.some(p => p.status === 'paid');
  const canChange = booking.status === 'pending_payment' && !anyPaid
    && ((hasSingle && !singleExpired) || hasOtherOptions);
  const needsDeposit = !isCurrentSingle && (booking.depositRequired || booking.depositPercent > 0 || booking.depositAmount);
  const depositLabel = booking.depositAmount
    ? `${Number(booking.depositAmount).toFixed(2)} €/persona`
    : `${booking.depositPercent}%`;

  // Cambiar la forma de pago NO cobra nada: actualiza la reserva y recarga.
  const changeMethod = async (newPlanId) => {
    setError(''); setLoading('change-' + newPlanId);
    try {
      const id = booking.bookingId || booking.id;
      await api.changePaymentPlan(token, id, newPlanId);
      setChangingMethod(false);
      onPaid?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  if (isCompleted) {
    return (
      <div style={{ marginTop: '.8rem', padding: '.7rem 1rem', background: 'rgba(74,124,89,.1)', border: '1px solid rgba(74,124,89,.25)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', gap: '.6rem' }}>
        <span style={{ fontSize: '1rem' }}>✅</span>
        <div>
          <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--sage)' }}>Pago completado</div>
          <div style={{ fontSize: '.72rem', color: 'var(--mid)' }}>{totalPaid.toFixed(2)} € pagados</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '.8rem', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
      <div style={{ padding: '.6rem 1rem', background: 'rgba(232,164,48,.08)', borderBottom: '1px solid rgba(232,164,48,.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--amber)' }}>
          💳 Pagos pendientes
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
          {totalDue > 0 && (
            <span style={{ fontSize: '.75rem', color: 'var(--mid)' }}>
              {totalPaid.toFixed(2)} € de {totalDue.toFixed(2)} € pagados
            </span>
          )}
          {canChange && (
            <button className="btn btn-ghost btn-sm"
              style={{ fontSize: '.7rem', padding: '.25rem .6rem' }}
              onClick={() => setChangingMethod(v => !v)}>
              {changingMethod ? '← Volver' : '⇄ Cambiar método'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ margin: '.6rem', marginBottom: 0 }}><span>⚠</span> {error}</div>}

      <div style={{ padding: '.6rem' }}>

        {/* Selector de cambio de método */}
        {changingMethod && (
          <div style={{ marginBottom: '.6rem', padding: '.7rem', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.12)', borderRadius: 'var(--radius)' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--sage)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.6rem' }}>
              Selecciona la nueva forma de pago
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
              {hasSingle && !singleExpired && (
                <button
                  className={`btn btn-sm ${isCurrentSingle ? 'btn-primary' : 'btn-secondary'} btn-full`}
                  onClick={() => changeMethod('single')}
                  disabled={loading === 'change-single' || isCurrentSingle}>
                  {loading === 'change-single' ? <><span className="spinner" /> Cambiando...</>
                    : isCurrentSingle ? '✓ Pago único (forma actual)' : '💳 Cambiar a pago único (importe completo)'}
                  {singleDueDate && !singleExpired && (
                    <span style={{ fontSize: '.65rem', marginLeft: '.4rem', opacity: .7 }}>
                      · hasta {singleDueDate.toLocaleDateString('es-ES')}
                    </span>
                  )}
                </button>
              )}
              {singleExpired && (
                <div style={{ fontSize: '.78rem', color: '#ff7a6a', padding: '.4rem .6rem', background: 'rgba(214,60,42,.08)', borderRadius: 6 }}>
                  ⚠ El plazo para pago único venció el {singleDueDate.toLocaleDateString('es-ES')}
                </div>
              )}
              {paymentOptions.map(opt => (
                <button key={opt.id}
                  className={`btn btn-sm ${currentMethod === opt.id ? 'btn-primary' : 'btn-secondary'} btn-full`}
                  onClick={() => changeMethod(opt.id)}
                  disabled={loading === 'change-' + opt.id || currentMethod === opt.id}>
                  {loading === 'change-' + opt.id ? <><span className="spinner" /> Cambiando...</>
                    : currentMethod === opt.id
                      ? `✓ ${opt.label} (forma actual)`
                      : `📅 Cambiar a: ${opt.label}`}
                </button>
              ))}
              <div style={{ fontSize: '.7rem', color: 'var(--mid)' }}>
                Cambiar la forma de pago no cobra nada: después verás los nuevos importes para pagarlos.
              </div>
            </div>
          </div>
        )}

        {/* Sin plazos aún — opciones iniciales */}
        {!changingMethod && payments.length === 0 && booking.status !== 'confirmed' && (
          <div style={{ padding: '.4rem', display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
            <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginBottom: '.2rem' }}>
              Completa el pago para confirmar tu reserva:
            </div>
            {/* Pago único: un solo botón con el importe completo */}
            {isCurrentSingle && hasSingle && !singleExpired && (
              <button className="btn btn-primary btn-full btn-sm"
                onClick={() => pay('single')} disabled={loading === 'single'}>
                {loading === 'single' ? <><span className="spinner" /> Redirigiendo...</> : '💳 Pagar ahora (importe completo)'}
              </button>
            )}
            {isCurrentSingle && (singleExpired || !hasSingle) && hasOtherOptions && (
              <div style={{ fontSize: '.78rem', color: '#ff7a6a', padding: '.4rem .6rem', background: 'rgba(214,60,42,.08)', borderRadius: 6 }}>
                ⚠ El pago único ya no está disponible. Usa «⇄ Cambiar método» para elegir un plan a plazos.
              </div>
            )}
            {/* Plan a plazos con señal: primero se paga la señal */}
            {needsDeposit && (
              <>
                <button className="btn btn-primary btn-full btn-sm"
                  onClick={() => pay('deposit')} disabled={loading === 'deposit'}>
                  {loading === 'deposit' ? <><span className="spinner" /> Redirigiendo...</> : `🔒 Pagar la señal (${depositLabel})`}
                </button>
                {optionPlans.length > 0 && (
                  <div style={{ fontSize: '.72rem', color: 'var(--mid)', padding: '.3rem .5rem' }}>
                    Después de la señal pagarás: {optionPlans.map(p =>
                      `${p.label} (${p.amount}€/persona${p.dueDate ? ` hasta el ${new Date(p.dueDate).toLocaleDateString('es-ES')}` : ''})`
                    ).join(' · ')}
                  </div>
                )}
              </>
            )}
            {/* Plan a plazos sin señal (respaldo: normalmente ya existen los plazos abajo) */}
            {!isCurrentSingle && !needsDeposit && optionPlans.map((plan, i) => (
              <button key={plan.id || i} className="btn btn-primary btn-full btn-sm"
                onClick={() => pay(plan.id)} disabled={loading === plan.id}>
                {loading === plan.id ? <><span className="spinner" /> Redirigiendo...</>
                  : `📅 Pagar: ${plan.label} — ${plan.amount}€/persona`}
              </button>
            ))}
          </div>
        )}

        {/* Estancia por transferencia: datos bancarios */}
        {porTransferencia && !isCompleted && (
          <div style={{
            background: 'rgba(232,164,48,.08)', border: '1px solid rgba(232,164,48,.25)',
            borderRadius: 10, padding: '.7rem .8rem', marginBottom: '.5rem',
          }}>
            <div style={{ fontWeight: 700, color: 'var(--white)', marginBottom: '.35rem' }}>🏦 Pago por transferencia</div>
            <div style={{ fontSize: '.82rem', color: 'var(--mid)', lineHeight: 1.7 }}>
              <div>Beneficiario: <strong style={{ color: 'var(--white)' }}>{dt.beneficiario || '—'}</strong></div>
              <div>Cuenta (IBAN): <strong style={{ color: 'var(--white)', letterSpacing: '.5px' }}>{dt.iban || '—'}</strong></div>
              <div>Concepto: <strong style={{ color: 'var(--white)' }}>{dt.concepto || '—'}</strong></div>
            </div>
            <div style={{ fontSize: '.76rem', color: 'var(--mid)', marginTop: '.45rem', lineHeight: 1.6 }}>
              Indica ese <strong>concepto</strong> en la transferencia. Puedes pagar primero la señal y luego el resto; confirmaremos cada pago al recibirlo.
            </div>
          </div>
        )}

        {/* Plazos definidos */}
        {!changingMethod && payments.map((p, i) => (
          <div key={p.planId || i} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '.55rem .6rem', borderRadius: 8,
            background: p.status === 'paid' ? 'rgba(74,124,89,.08)' : 'rgba(255,255,255,.02)',
            marginBottom: '.3rem',
          }}>
            <div>
              <div style={{ fontSize: '.82rem', fontWeight: 600, color: p.status === 'paid' ? 'var(--sage)' : 'var(--white)' }}>
                {p.status === 'paid' ? '✓ ' : ''}{p.label}
              </div>
              <div style={{ fontSize: '.7rem', color: 'var(--mid)', marginTop: '.1rem' }}>
                {p.amount?.toFixed(2)} €
                {p.dueDate && p.status !== 'paid' && (
                  <span style={{ color: new Date(p.dueDate) < new Date() ? '#ff7a6a' : 'var(--mid)' }}>
                    {' '}· Fecha límite: {new Date(p.dueDate).toLocaleDateString('es-ES')}
                  </span>
                )}
                {p.paidAt && (() => {
                  const d = p.paidAt?.seconds ? new Date(p.paidAt.seconds * 1000) : new Date(p.paidAt);
                  return ` · Pagado el ${isNaN(d) ? '' : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`;
                })()}
              </div>
            </div>
            {p.status === 'pending' && !porTransferencia && (
              <button className="btn btn-primary btn-sm"
                onClick={() => pay(p.planId)} disabled={loading === p.planId}>
                {loading === p.planId ? <span className="spinner" /> : 'Pagar'}
              </button>
            )}
            {p.status === 'pending' && porTransferencia && (
              <span className="badge" style={{ background: 'rgba(232,164,48,.15)', color: 'var(--amber)' }}>Pendiente</span>
            )}
            {p.status === 'paid' && <span className="badge badge-green">Pagado</span>}
          </div>
        ))}

        {!changingMethod && pending > 0 && payments.length > 0 && (
          <div style={{ padding: '.4rem .6rem', fontSize: '.75rem', color: 'var(--mid)', borderTop: '1px solid rgba(122,171,138,.08)', marginTop: '.3rem' }}>
            Pendiente de pago: <strong style={{ color: 'var(--amber)' }}>{pending.toFixed(2)} €</strong>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal: editar datos de los participantes de una reserva ──
function EditMembersModal({ booking, onClose, onSaved }) {
  const { token } = useAuth();
  // El formulario trabaja sobre el member "plano" (igual que en la inscripción):
  // se despliega formData y se garantiza birthYear en reservas antiguas.
  const [members, setMembers] = useState(() => (booking.members || []).map(m => ({
    ...m,
    ...(m.formData || {}),
    birthYear: m.birthYear || (m.fechaNacimiento ? parseInt(m.fechaNacimiento.slice(0, 4)) : ''),
  })));
  const [memberFields, setMemberFields] = useState([]);
  const [excludedMandatory, setExcludedMandatory] = useState([]);
  const [packList, setPackList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Esquema del evento (preguntas personalizadas) y paquetes de preguntas
  useEffect(() => {
    api.request('GET', `/api/bookings/${booking.bookingId || booking.id}`, null, token)
      .then(d => {
        const schema = d.eventSchema || {};
        setMemberFields((schema.formSchema || []).filter(f => f.scope === 'member' || !f.scope));
        setExcludedMandatory(schema.excludedMandatory || []);
      })
      .catch(() => {});
    api.request('GET', '/api/admin/question-packs', null, token)
      .then(d => setPackList(d.packs || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const save = async () => {
    setError('');
    for (let i = 0; i < members.length; i++) {
      if (!members[i].firstName?.trim() && !members[i].name?.trim()) {
        setError(`La persona ${i + 1} necesita un nombre.`); return;
      }
    }
    setSaving(true);
    try {
      // Igual que en la inscripción: nombre, edad y rol se derivan, y las
      // respuestas del formulario del evento se consolidan en formData.
      const clean = members.map(m => {
        const birthYear = parseInt(m.birthYear) || null;
        const age = birthYear ? new Date().getFullYear() - birthYear : (m.age ?? null);
        const formData = { ...(m.formData || {}) };
        memberFields.forEach(f => { if (m[f.id] !== undefined) formData[f.id] = m[f.id]; });
        return {
          ...m,
          birthYear, age,
          role: age !== null && age < 18 ? 'child' : 'adult',
          name: `${m.firstName || ''} ${m.lastName1 || ''} ${m.lastName2 || ''}`.trim() || m.name,
          formData,
        };
      });
      await api.updateMyBooking(token, booking.bookingId || booking.id, clean);
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !saving && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div className="section-label">Editar datos</div>
            <h2>{booking.eventName || 'Reserva'}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}
        {members.map((m, i) => (
          <div className="member-box" key={i}>
            <div className="member-label" style={{ marginBottom: '.5rem' }}>
              Persona {i + 1}{m.name ? ` — ${m.name}` : ''}
            </div>
            <DynamicForm
              fields={memberFields}
              value={m}
              onChange={val => setMembers(ms => ms.map((x, idx) => idx === i ? val : x))}
              prefix={`edit-${i}`}
              packs={packList}
              token={token}
              excludedMandatory={excludedMandatory}
            />
          </div>
        ))}
        <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'flex-end', marginTop: '.8rem' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <><span className="spinner" /> Guardando...</> : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyBookings() {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [campamento, setCampamento] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // reserva en edición de datos

  // Detectar retorno de Stripe: se captura UNA vez en estado (si se leyera de
  // la URL en cada render, el replaceState de abajo lo borraría antes de que
  // la página, aún cargando, llegara a pintar el banner).
  const [pagoStatus, setPagoStatus] = useState(() => new URLSearchParams(window.location.search).get('pago'));

  // Clean URL param (el banner sigue visible gracias al estado)
  useEffect(() => {
    if (pagoStatus) {
      const url = new URL(window.location);
      url.searchParams.delete('pago');
      window.history.replaceState({}, '', url);
    }
  }, [pagoStatus]);

  const load = () => {
    setLoading(true);
    api.myBookings(token)
      // Las caducadas por falta de pago se muestran (con opción de reactivar);
      // el resto de canceladas, no.
      .then(d => setBookings((d.bookings || []).filter(b =>
        b.status !== 'cancelled' || b.cancelReason === 'payment_timeout')))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    // Reservas de campamento web (campamentoaldealix.com). No bloquea ni rompe
    // la carga de las reservas nativas si falla.
    api.myCampamentoReservas(token)
      .then(r => setCampamento((r.reservas || []).filter(x => x.estado !== 'cancelled')))
      .catch(() => setCampamento([]));
  };

  useEffect(load, [token]);

  const cancel = async (id) => {
    if (!window.confirm('¿Seguro que quieres cancelar esta inscripción?')) return;
    try { 
      await api.cancelBooking(token, id); 
      load(); 
    } catch (e) { 
      setError(e.message); 
    }
  };

  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  return (
    <div className="main-content page-enter">
      <div style={{ marginBottom: '1.8rem' }}>
        <div className="section-label">Tu historia</div>
        <h1>Mis reservas</h1>
        <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.95rem', marginTop: '.2rem' }}>
          Historial de tus inscripciones en la aldea
        </p>
      </div>

      {/* Feedback de vuelta de Stripe */}
      {pagoStatus === 'ok' && (
        <div className="alert alert-success" style={{ marginBottom: '1.2rem' }}>
          <span>✅</span> ¡Pago completado correctamente! Tu reserva ha sido confirmada.
        </div>
      )}
      {pagoStatus === 'cancelado' && (
        <div className="alert alert-warn" style={{ marginBottom: '1.2rem' }}>
          <span>⚠</span> El pago fue cancelado. Puedes intentarlo de nuevo desde tu reserva.
        </div>
      )}

      {editing && (
        <EditMembersModal booking={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

      {bookings.length === 0 && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)', fontStyle: 'italic' }}>
          Todavía no tienes ninguna reserva.
        </div>
      )}

      {bookings.map(b => (
        <div key={b.bookingId} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)', padding: '1.1rem 1.3rem', marginBottom: '.7rem', borderRadius: 'var(--radius)', boxShadow: '0 4px 20px rgba(0,0,0,.4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '.8rem', marginBottom: '.4rem' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '.95rem' }}>{b.eventName || b.eventId}</div>
              {b.checkInDate && b.checkOutDate && (
                <div style={{ fontSize: '.8rem', color: 'var(--sage)', marginTop: '.2rem' }}>
                  🗓 Llegada {new Date(b.checkInDate).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'long' })}
                  {' → '}salida {new Date(b.checkOutDate).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'long' })}
                </div>
              )}
              <div style={{ fontSize: '.8rem', color: 'var(--mid)', marginTop: '.2rem' }}>
                {b.members?.length || b.totalMembers || 1} inscrito{(b.totalMembers || 1) !== 1 ? 's' : ''}
                {b.registrationType && ` · ${b.registrationType === 'individual' ? 'Individual' : 'Grupal'}`}
              </div>
              <div style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.2rem', display: 'flex', flexDirection: 'column', gap: '.1rem' }}>
                <span>
                  Reserva evento{' '}
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", color: 'var(--sage)' }}>
                    {b.eventId?.slice(0, 8).toUpperCase()}
                  </span>
                </span>
                <span>
                  Nº reserva{' '}
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", color: 'var(--sage)' }}>
                    #{b.bookingId?.slice(0, 8).toUpperCase()}
                  </span>
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '.4rem' }}>
              <span className={`badge ${
                b.status === 'confirmed' ? 'badge-green' :
                b.status === 'cancelled' ? 'badge-danger' :
                b.status === 'pending_payment' ? 'badge-amber' :
                'badge-stone'
              }`}>
                {b.status === 'confirmed' ? 'Confirmada' :
                 b.status === 'cancelled' ? (b.cancelReason === 'payment_timeout' ? '⏰ Caducada sin pagar' : 'Cancelada') :
                 b.status === 'pending_payment' ? '⏳ Pago pendiente' :
                 b.status}
              </span>
              {b.paymentStatus === 'completed' && (
                <span className="badge badge-green">✓ Pagado</span>
              )}
              {b.paymentStatus && b.paymentStatus !== 'completed' && b.status === 'confirmed' && (
                <span className="badge badge-amber">💳 Pago parcial</span>
              )}
              {(b.status === 'confirmed' || b.status === 'pending_payment') && (
                <div style={{ display: 'flex', gap: '.3rem' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(b)}>✎ Editar datos</button>
                  <button className="btn btn-danger btn-sm" onClick={() => cancel(b.bookingId || b.id)}>Cancelar</button>
                </div>
              )}
            </div>
          </div>

          {b.members?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.3rem', marginTop: '.4rem' }}>
              {b.members.map((m, i) => (
                <span key={i} style={{ fontSize: '.72rem', background: 'rgba(122,171,138,.1)', border: '1px solid rgba(122,171,138,.2)', padding: '.18rem .55rem', borderRadius: 4, color: 'var(--sage)' }}>
                  {m.name || `${m.firstName} ${m.lastName1}`.trim()} {m.birthYear && `(${new Date().getFullYear() - m.birthYear})`}
                </span>
              ))}
            </div>
          )}

          {/* Caducada por falta de pago: se puede reactivar si quedan plazas */}
          {b.status === 'cancelled' && b.cancelReason === 'payment_timeout' && (
            <div style={{ marginTop: '.8rem', padding: '.7rem 1rem', background: 'rgba(214,60,42,.07)', border: '1px solid rgba(214,60,42,.2)', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: '.82rem', color: 'var(--mid)', marginBottom: '.5rem' }}>
                Esta reserva caducó porque no se completó el pago a tiempo. Si quedan plazas, puedes
                reactivarla: tendrás 48 horas para pagar.
              </div>
              <button className="btn btn-primary btn-sm"
                onClick={async () => {
                  try { await api.reactivateBooking(token, b.bookingId || b.id); load(); }
                  catch (e) { setError(e.message); }
                }}>
                🔄 Reactivar y pagar
              </button>
            </div>
          )}

          {/* Panel de pagos si el evento requiere pago */}
          {b.status !== 'cancelled' && (b.requiresPayment || b.totalDue > 0 || b.payments?.length > 0 || b.status === 'pending_payment') && (
            <PaymentPanel booking={b} onPaid={load} />
          )}
        </div>
      ))}

      {/* Inscripciones de campamento web (campamentoaldealix.com) */}
      {campamento.length > 0 && (
        <div style={{ marginTop: '2.2rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <div className="section-label">Campamento de verano</div>
            <h2 style={{ margin: '.2rem 0 0' }}>🏕️ Inscripciones de campamento</h2>
          </div>

          {campamento.map(r => {
            const semanas = (r.matchedEvents || []).map(ev => {
              const m = ev.name?.match(/Semana\s*\d+/i);
              return m ? m[0] : ev.name;
            }).filter(Boolean);

            const fmt = (d) => {
              if (!d) return null;
              const date = new Date(d);
              return isNaN(date) ? null : date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
            };
            const fechaIn = fmt(r.checkInDate);
            const fechaOut = fmt(r.checkOutDate);

            const remaining = r.remaining || 0;
            const pagoLabel = r.pagoCompletado
              ? '✅ Pagado'
              : remaining > 0
                ? `🟡 Falta ${remaining}€`
                : '🟡 Pendiente';

            const btnLabel = !r.datosCompletos
              ? 'Completar datos y pagar'
              : !r.pagoCompletado
                ? 'Pagar resto + elegir bus'
                : 'Ver inscripción';

            return (
              <div key={r.externalId || r.token} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)', padding: '1.1rem 1.3rem', marginBottom: '.7rem', borderRadius: 'var(--radius)', boxShadow: '0 4px 20px rgba(0,0,0,.4)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '.8rem', marginBottom: '.4rem' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '.95rem' }}>
                      {r.customerName || 'Inscripción de campamento'}
                    </div>
                    {semanas.length > 0 && (
                      <div style={{ fontSize: '.8rem', color: 'var(--mid)', marginTop: '.2rem' }}>
                        {semanas.join(' · ')}
                      </div>
                    )}
                    {(fechaIn || fechaOut) && (
                      <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.2rem' }}>
                        {fechaIn}{fechaIn && fechaOut ? ' → ' : ''}{fechaOut}
                      </div>
                    )}
                    <div style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.2rem' }}>
                      Nº reserva{' '}
                      <span style={{ fontFamily: "'JetBrains Mono',monospace", color: 'var(--sage)' }}>
                        #{r.externalId}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '.4rem' }}>
                    <span className={`badge ${r.datosCompletos ? 'badge-green' : 'badge-amber'}`}>
                      Datos: {r.datosCompletos ? '✅ Completos' : '🟡 Pendientes'}
                    </span>
                    <span className={`badge ${r.pagoCompletado ? 'badge-green' : 'badge-amber'}`}>
                      Pago: {pagoLabel}
                    </span>
                  </div>
                </div>

                <div style={{ marginTop: '.6rem' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => { window.location.href = `/completar?token=${r.token}`; }}>
                    {btnLabel}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
